// Geteilte Bausteine für die Rechner: Zahlenfeld, Warnungen, Ergebnis-Aktionen.
import React from "react";
import { AlertTriangle, Copy, Printer, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { track } from "@/lib/track";

export function NumberField({ id, label, value, onChange, unit = "cm", placeholder, min = 0, step = "0.1", hint }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-sm font-medium">
        {label}
      </Label>
      <div className="relative">
        <Input
          id={id}
          data-testid={`field-${id}`}
          type="number"
          inputMode="decimal"
          min={min}
          step={step}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          className="h-11 pr-12 text-base"
        />
        {unit && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
            {unit}
          </span>
        )}
      </div>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

export function Warnings({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <div
      data-testid="calc-warnings"
      className="rounded-xl border border-warning/40 bg-warning/10 p-3 space-y-1.5"
      role="alert"
    >
      {items.map((w, i) => (
        <div key={i} className="flex items-start gap-2 text-sm text-foreground">
          <AlertTriangle className="h-4 w-4 text-warning shrink-0 mt-0.5" />
          <span>{w}</span>
        </div>
      ))}
    </div>
  );
}

export function ResultRow({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1.5 border-b border-border/60 last:border-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-semibold tabular-nums">{value}</span>
    </div>
  );
}

// Kopieren + Drucken. `text` ist der Klartext fürs Clipboard.
export function ResultActions({ text }) {
  const [copied, setCopied] = React.useState(false);

  const copy = async () => {
    track("result_action", { action: "copy" });
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      // Fallback: Auswahl per Textarea
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
      } catch (_) {}
      document.body.removeChild(ta);
    }
  };

  return (
    <div className="flex flex-wrap gap-2 pt-2">
      <Button type="button" variant="outline" size="sm" onClick={copy} data-testid="btn-copy">
        {copied ? <Check className="h-4 w-4 mr-1.5" /> : <Copy className="h-4 w-4 mr-1.5" />}
        {copied ? "Kopiert" : "Ergebnis kopieren"}
      </Button>
      <Button type="button" variant="outline" size="sm" onClick={() => { track("result_action", { action: "print" }); window.print(); }} data-testid="btn-print">
        <Printer className="h-4 w-4 mr-1.5" /> Drucken
      </Button>
    </div>
  );
}

export function Steps({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <ol className="space-y-2 mt-2" data-testid="calc-steps">
      {items.map((s, i) => (
        <li key={i} className="flex items-start gap-2.5 text-sm">
          <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-medium">
            {i + 1}
          </span>
          <span className="text-muted-foreground leading-relaxed">{s}</span>
        </li>
      ))}
    </ol>
  );
}
